import { Component, h, Host, Prop, forceUpdate, Watch, Event, EventEmitter, State } from "@stencil/core";
import { i18nProvider } from "@trumpf/ux.i18n";
import loadTranslations, { TranslationKey } from "../../utils/loadTranslations";
import Fragment from "../../utils/Fragment";
import { CreateCaseRequest, createRequestData, PrefillData } from "../../api/publicApi/createCase";
import { getPublicApiClient, PublicApiClient } from "../../api/publicApi/PublicApiClient";
import { EnvConfig, getEnvConfig, setEnv } from "../../api/config";
import { StepBack, StepNavigation, StepNext, stepState } from "./steps";
import { createApiStore, loadFormDataFromAssetManagementApi, loadFormDataFromPublicApi } from "./api-store";
import { createAuthStore, SilentAuth, whenAuthenticated } from "./auth";
import { createFormStore } from "./form/form-store";
import { ErrorNumberInput } from "./form/ErrorNumberInput";
import { EmailInput, FirstNameInput, LastNameInput, PhoneInput, SalutationInput } from "./form/ContactInputs";
import {
    InstallationCheckbox,
    LicenseCheckbox,
    SoftwareClassificationSelect,
    SoftwareModuleSelect,
    SoftwareTypeSelect
} from "./form/SoftwareInputs";
import { ApplicationIssueSelect, ApplicationOpticsSelect, ApplicationSelect } from "./form/ApplicationInputs";
import { MachineSelect } from "./form/MachineSelect";
import { NotificationTypeSelect } from "./form/NotificationTypeSelect";
import { ProductTechnologyType } from "../../api/productTechnology";
import { AssetManagementApiClient, getAssetManagementApiClient } from "../../api/assetManagementApi/assetManagementApiClient";

/**
 * @slot contacts-button - Slot for native contacts button to select contact data from phone address book
 * @slot connector - Slot for the connector that handles sending the form data to a backend
 */
@Component({
    tag: "mf-create-service-case",
    styleUrl: "create-service-case.css",
    shadow: false
})
export class MfCreateServiceCase {
    /**
     * If true, the form will be displayed in multiple steps
     */
    @Prop() multipleSteps: boolean = false;
    /**
     * Enable file upload in the form
     */
    @Prop() fileUpload: boolean = false;
    /**
     * data that should be prefilled in the form
     * @optional
     */
    @Prop() prefillData: PrefillData | null = null;
    /**
     * application name that will be used for identification of the source when sending the form
     */
    // @Prop() applicationName: string = "mf-service-calls";
    /**
     * The locale to use for translations and the phone input
     */
    @Prop({ reflect: true }) locale: string = "en-US";
    /**
     * Environment string to decide which endpoints to use
     */
    @Prop() env: string = "prod";
    /**
     * use onprem Public API instead of azure
     * @deprecated only used until April 2025
     */
    @Prop() legacyApi: boolean = false;
    /**
     * disable silent auth - then the application needs to supply the auth token via @trumpf/ux.utils
     * @optional
     */
    @Prop() disableSilentAuth: boolean = false;

    @State() tbdbError: string | null = null;

    private config: EnvConfig;
    private publicApiClient: PublicApiClient;
    private assetManagementApiClient: AssetManagementApiClient;
    private apiStore = createApiStore();
    private authStore = createAuthStore();
    private formStore = createFormStore();
    private i18nSubscriberId: string;
    private form: HTMLFormElement;
    private callId: string = crypto.randomUUID(); // this browser API only works on localhost and over https
    private unsubscribeAuth: () => void;
    private unsubscribeToTbdbToken: () => void;

    connectedCallback() {
        setEnv(`${this.env}${this.legacyApi ? "Legacy" : ""}`);
        this.config = getEnvConfig();
        this.publicApiClient = getPublicApiClient(this.config);
        this.unsubscribeToTbdbToken = this.publicApiClient.subscribeToTbdbToken((event) => {
            if (event.success) {
                loadFormDataFromPublicApi(this.apiStore, this.publicApiClient);
            } else {
                this.tbdbError = event.errorMessage || "Error getting TBDB token";
            }
        });
        this.assetManagementApiClient = getAssetManagementApiClient(this.config);
        this.loadTranslations();
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
        this.unsubscribeAuth = whenAuthenticated(() =>
            loadFormDataFromAssetManagementApi(this.apiStore, this.assetManagementApiClient)
        );
        this.apiStore.onChange("notificationTypesLoading", (notificationTypesLoading) => {
            if (!notificationTypesLoading) {
                this.formStore.state.selectedNotificationType = this.prefillData?.notificationType || null;
            }
        });
    }

    disconnectedCallback() {
        this.unsubscribeAuth();
        i18nProvider.unsubscribe(this.i18nSubscriberId);
        if (this.unsubscribeToTbdbToken) {
            this.unsubscribeToTbdbToken();
        }
    }

    @Watch("locale")
    onLocaleChange() {
        if (this.locale !== i18nProvider.language) {
            this.loadTranslations();
        }
    }

    @Watch("prefillData")
    onPrefillDataChange() {
        const notificationType = this.prefillData?.notificationType;
        if (notificationType) {
            this.formStore.state.selectedNotificationType = notificationType;
        }
    }

    /**
     * Event that is fired when the form is submitted
     */
    @Event({
        eventName: "mfServiceCaseSubmit",
        bubbles: true,
        composed: true
    })
    serviceCaseSubmit: EventEmitter<CreateCaseRequest>;

    /**
     * Event that is fired when the form is cancelled and the form should be closed
     */
    @Event({
        eventName: "mfServiceCaseCancel",
        bubbles: true,
        composed: true
    })
    serviceCaseCancel: EventEmitter<void>;

    private loadTranslations() {
        loadTranslations(this.locale).then((locale) => {
            i18nProvider.changeLanguage(locale);
            this.locale = locale;
        });
    }

    private onSubmit(event: Event) {
        event.preventDefault();
        const { softwares } = this.apiStore.state;
        const { form, callId } = this;
        const data = createRequestData({
            form,
            callId,
            softwares,
            selectedMachineTechnology: this.formStore.state.selectedMachineTechnology as ProductTechnologyType
        });
        this.serviceCaseSubmit.emit(data);
        this.formStore.state.formIsSubmitted = true;
        if (this.config.logging) {
            // eslint-disable-next-line no-console
            console.log("Form submitted", data);
        }
    }

    private onCancel() {
        this.serviceCaseCancel.emit();
    }

    render() {
        const t = (key: TranslationKey, options: unknown = undefined) =>
            i18nProvider.t(`mf-service-calls:${String(key)}`, options as Record<string, unknown>);
        const { loading } = this.apiStore.state;
        const { isAuthenticating, authError } = this.authStore.state;
        const { prefillData, multipleSteps } = this;
        const {
            showErrorNumbers,
            formIsSubmitted,
            showApplications,
            showSoftwareModules,
            showMachines,
            showSoftwareFields,
            showWeldingApplicationFields
        } = this.formStore.state;
        const showStep1 =
            ((multipleSteps && stepState.step === 1) || !multipleSteps) && !(loading || isAuthenticating)
                ? {}
                : { display: "none" };
        const showStep2 =
            ((multipleSteps && stepState.step === 2) || !multipleSteps) && !(loading || isAuthenticating)
                ? {}
                : { display: "none" };

        return (
            <Host>
                {!formIsSubmitted && (
                    <ux-form data-validation-required--msg={t("validation.requiredField")}>
                        <form onSubmit={(e) => this.onSubmit(e)} ref={(el) => (this.form = el as HTMLFormElement)}>
                            <div class="ux-u-text-align--center ux-u-margin-top-60 ux-u-margin-bottom-60">
                                <div class="ux-text-headline ux-text-headline--giant">{t("headline.createCase")}</div>
                            </div>
                            <StepNavigation t={t} multipleSteps={multipleSteps} />
                            {(loading || isAuthenticating) && <ux-loader variant={"spinner"}></ux-loader>}
                            {authError && (
                                <div class="ux-u-text-align--center ux-text-normal ux-u-margin-top-20 ux-u-margin-bottom-20">
                                    {t("auth.error")}
                                </div>
                            )}
                            <div class="ux-row ux-u-flex-jc-center" style={showStep1}>
                                <div class="ux-col-12 ux-col-md-10">
                                    <div class="ux-row">
                                        <div class="ux-col-12 ux-col-md-6">
                                            <div class="ux-u-margin-bottom-20">
                                                <NotificationTypeSelect
                                                    t={t}
                                                    prefillData={prefillData}
                                                    formStore={this.formStore}
                                                    apiStore={this.apiStore}
                                                />
                                            </div>
                                        </div>
                                        {showMachines && (
                                            <div class="ux-col-12 ux-col-md-6">
                                                <div class="ux-u-margin-bottom-20">
                                                    <MachineSelect
                                                        t={t}
                                                        prefillData={prefillData}
                                                        formStore={this.formStore}
                                                        apiStore={this.apiStore}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {showApplications && (
                                        <Fragment>
                                            <div class="ux-row">
                                                <div class="ux-col-12 ux-col-md-6">
                                                    <div class="ux-u-margin-bottom-20">
                                                        <ApplicationSelect t={t} formStore={this.formStore} />
                                                    </div>
                                                </div>
                                            </div>
                                            {showWeldingApplicationFields && (
                                                <div class="ux-row">
                                                    <div class="ux-col-12 ux-col-md-6">
                                                        <div class="ux-u-margin-bottom-20">
                                                            <ApplicationOpticsSelect t={t} />
                                                        </div>
                                                    </div>
                                                    <div class="ux-col-12 ux-col-md-6">
                                                        <div class="ux-u-margin-bottom-20">
                                                            <ApplicationIssueSelect t={t} />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </Fragment>
                                    )}
                                    {showSoftwareFields && (
                                        <Fragment>
                                            <div class="ux-row">
                                                <div class="ux-col-12 ux-col-md-6">
                                                    <div class="ux-u-margin-bottom-20">
                                                        <SoftwareClassificationSelect t={t} prefillData={prefillData} />
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="ux-row">
                                                <div class="ux-col-12 ux-col-md-6">
                                                    <div class="ux-u-margin-bottom-20">
                                                        <InstallationCheckbox t={t} prefillData={prefillData} />
                                                    </div>
                                                </div>
                                                <div class="ux-col-12 ux-col-md-6">
                                                    <div class="ux-u-margin-bottom-20">
                                                        <LicenseCheckbox t={t} prefillData={prefillData} />
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="ux-row">
                                                <div class="ux-col-12 ux-col-md-6">
                                                    <div class="ux-u-margin-bottom-20">
                                                        <SoftwareTypeSelect
                                                            t={t}
                                                            prefillData={prefillData}
                                                            formStore={this.formStore}
                                                            apiStore={this.apiStore}
                                                        />
                                                    </div>
                                                </div>
                                                {showSoftwareModules && (
                                                    <div class="ux-col-12 ux-col-md-6">
                                                        <div class="ux-u-margin-bottom-20">{/* this is only for styles */}</div>
                                                    </div>
                                                )}
                                            </div>
                                            <div class="ux-row">
                                                {showSoftwareModules && (
                                                    <div class="ux-col-12 ux-col-md-6">
                                                        <div class="ux-u-margin-bottom-20">
                                                            <SoftwareModuleSelect
                                                                t={t}
                                                                prefillData={prefillData}
                                                                formStore={this.formStore}
                                                                apiStore={this.apiStore}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </Fragment>
                                    )}
                                    {showErrorNumbers && (
                                        <div class="ux-row">
                                            <div class="ux-col-12">
                                                <ErrorNumberInput t={t} prefillData={prefillData} />
                                            </div>
                                        </div>
                                    )}
                                    <div class="ux-row">
                                        <div class="ux-col-12">
                                            <div class="ux-u-margin-bottom-20">
                                                <ux-textarea-field
                                                    label={t("field.message.label")}
                                                    required
                                                    data-validation-required--msg={t("validation.requiredField")}
                                                    name="description"
                                                ></ux-textarea-field>
                                            </div>
                                        </div>
                                    </div>

                                    {this.fileUpload && (
                                        <div class="ux-row">
                                            <div class="ux-col-12">
                                                <ux-file-upload
                                                    max-file-size="2"
                                                    label={t("field.fileUpload.label")}
                                                    accepted-files="image/*,application/pdf,text/html"
                                                    max-files="4"
                                                ></ux-file-upload>
                                            </div>
                                        </div>
                                    )}

                                    <StepNext t={t} multipleSteps={multipleSteps} onCancel={() => this.onCancel()} />
                                </div>
                            </div>

                            <div class="ux-row ux-u-flex-jc-center" style={showStep2}>
                                <div class="fields-wrapper ux-col-12 ux-col-md-10">
                                    <div>
                                        <slot name="contacts-button"></slot>
                                    </div>
                                    <div class="ux-row">
                                        <div class="ux-col-12 ux-col-md-6">
                                            <div class="ux-u-margin-bottom-20">
                                                <SalutationInput t={t} prefillData={prefillData} />
                                            </div>
                                        </div>
                                    </div>

                                    <div class="ux-row">
                                        <div class="ux-col-12 ux-col-md-6">
                                            <div class="ux-u-margin-bottom-20">
                                                <FirstNameInput t={t} prefillData={prefillData} />
                                            </div>
                                        </div>
                                        <div class="ux-col-12 ux-col-md-6">
                                            <div class="ux-u-margin-bottom-20">
                                                <LastNameInput t={t} prefillData={prefillData} />
                                            </div>
                                        </div>
                                    </div>

                                    <div class="ux-row">
                                        <div class="ux-col-12 ux-col-md-6">
                                            <div class="ux-u-margin-bottom-20">
                                                <PhoneInput t={t} prefillData={prefillData} locale={this.locale} />
                                            </div>
                                        </div>
                                        <div class="ux-col-12 ux-col-md-6">
                                            <div class="ux-u-margin-bottom-20">
                                                <EmailInput t={t} prefillData={prefillData} />
                                            </div>
                                        </div>
                                    </div>

                                    <div class="ux-u-margin-bottom-20">
                                        <div class="ux-text-normal ux-text-normal--light-gray">
                                            <p>{t("validation.requiredFieldsExplanation")}</p>
                                        </div>
                                    </div>

                                    <div class="ux-u-d-flex ux-u-flex-jc-between ux-u-flex-dir-row-reverse">
                                        <ux-button type="submit" variant="primary">
                                            {t("button.submit")}
                                        </ux-button>

                                        <StepBack t={t} multipleSteps={multipleSteps} />
                                        {!multipleSteps && (
                                            <ux-button type="button" onClick={() => this.onCancel()} formnovalidate>
                                                {t("button.cancel")}
                                            </ux-button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </form>
                    </ux-form>
                )}
                <div style={{ display: formIsSubmitted ? "block" : "none" }}>
                    <slot name="connector" />
                </div>
                <SilentAuth config={this.config} disabled={this.disableSilentAuth} authStore={this.authStore} />
            </Host>
        );
    }
}
